export async function isHeicImage(file: File): Promise<boolean> {
  if (/\.(heic|heif)$/i.test(file.name)) return true;
  if (/^image\/hei[cf](?:-sequence)?$/i.test(file.type)) return true;

  try {
    const brand = new TextDecoder("utf-8")
      .decode((await file.slice(8, 12).arrayBuffer()))
      .replace("\0", " ")
      .trim();
    return ["mif1", "msf1", "heic", "heix", "hevc", "hevx"].includes(brand);
  } catch {
    return false;
  }
}

function canvasToFile(
  canvas: HTMLCanvasElement,
  name: string,
  quality = 0.9,
): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(new File([blob], name, { type: "image/jpeg" }))
          : reject(new Error("canvas_encode_failed")),
      "image/jpeg",
      quality,
    );
  });
}

// Fast path: Safari / iOS decode HEIC natively, so the browser can do the
// conversion with no wasm download at all. Fails fast elsewhere.
async function nativeDecodeToJpeg(file: File, name: string): Promise<File | null> {
  try {
    if (typeof createImageBitmap !== "function") return null;
    const bitmap = await createImageBitmap(file);
    const maxEdge = 2400;
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();
    return await canvasToFile(canvas, name);
  } catch {
    return null;
  }
}

// Converts HEIC/HEIF files to JPEG. Returns the original file unchanged for
// non-HEIC inputs. If every conversion path fails we deliberately return the
// original HEIC file rather than throwing — Gemini accepts image/heic, so the
// user is never blocked by a decoder problem.
export async function normalizeImageFile(file: File): Promise<File> {
  if (!(await isHeicImage(file))) return file;

  const newName = file.name.replace(/\.(heic|heif)$/i, ".jpg");

  const native = await nativeDecodeToJpeg(file, newName);
  if (native) return native;

  try {
    const { heicTo } = await import("heic-to");
    const blob = await heicTo({ blob: file, type: "image/jpeg", quality: 0.9 });
    return new File([blob], newName, { type: "image/jpeg" });
  } catch (primaryErr) {
    console.warn("heic-to conversion failed, trying fallback", primaryErr);
  }

  try {
    const heic2any = (await import("heic2any")).default;
    const converted = (await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 0.9,
    })) as Blob | Blob[];
    const blob = Array.isArray(converted) ? converted[0] : converted;
    return new File([blob], newName, { type: "image/jpeg" });
  } catch (err) {
    console.error("HEIC conversion failed; passing original through", err);
  }

  // Last resort: hand back the original bytes with an explicit HEIC mime type.
  return file.type ? file : new File([file], file.name, { type: "image/heic" });
}
